import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, ArrowLeft } from "lucide-react";
import { useState, useEffect, useMemo } from "react";

interface BatchItem {
  item_id: string;
  item_type: string;
  points: number;
  name: string;
  icon: string;
}

interface HistoryItem {
  type: string;
  points: number;
  created_at: string;
  entity_name: string;
  entity_icon: string;
  encrypted_note?: string;
  ref_name?: string;
  ref_icon?: string;
  streak_days?: number;
  streak_factor?: number;
  items?: BatchItem[];
}

interface DaySummary {
  date: string;
  weekday: string;
  scoreDelta: number;
  choresCompleted: number;
  choresFailed: number;
  rulesBroken: number;
  streakBonus: number;
  events: HistoryItem[];
}

function getEventStyle(type: string) {
  switch (type) {
    case "rule_broken":
      return { emoji: "⚠️", bg: "bg-app-danger/10", text: "text-app-danger" };
    case "chore_completed":
      return { emoji: "✅", bg: "bg-app-success/10", text: "text-app-success" };
    case "chore_overdue":
      return { emoji: "⏰", bg: "bg-app-warning/10", text: "text-app-warning" };
    case "rule_streak":
      return { emoji: "🔥", bg: "bg-app-info/10", text: "text-app-info" };
    case "punishment_redeemed":
      return { emoji: "💀", bg: "bg-app-accent/10", text: "text-app-accent" };
    case "reward_redeemed":
      return { emoji: "🎁", bg: "bg-app-accent/10", text: "text-app-accent" };
    case "session_purchase":
      return { emoji: "🛒", bg: "bg-app-accent/10", text: "text-app-accent" };
    case "session_purchase_reversed":
    case "punishment_reversed":
    case "reward_reversed":
      return { emoji: "↩️", bg: "bg-app-surface-raised", text: "text-app-text-muted" };
    default:
      return { emoji: "📝", bg: "bg-app-surface-raised", text: "text-app-text-muted" };
  }
}

function formatEventLabel(h: HistoryItem): string {
  const name = h.entity_name || "[Encrypted]";
  switch (h.type) {
    case "chore_completed": return `Completed ${name}`;
    case "chore_overdue": return `Overdue: ${name}`;
    case "rule_broken": return `Rule broken: ${name}${h.encrypted_note ? ` — ${h.encrypted_note}` : ""}`;
    case "rule_streak": return `${name} streak: ${h.streak_days}d (×${h.streak_factor})`;
    case "punishment_redeemed": return `Punishment: ${h.ref_name || name}`;
    case "reward_redeemed": return `Reward: ${h.ref_name || name}`;
    case "punishment_reversed": return `Reversed punishment: ${h.ref_name || name}`;
    case "reward_reversed": return `Reversed reward: ${h.ref_name || name}`;
    case "session_purchase": {
      const count = h.items?.length || 0;
      return `Session purchase (${count} item${count !== 1 ? "s" : ""})`;
    }
    case "session_purchase_reversed": {
      const count = h.items?.length || 0;
      return `Purchase reversed (${count} item${count !== 1 ? "s" : ""})`;
    }
    default: return h.type?.replace(/_/g, " ") || "Event";
  }
}

function groupByDay(history: HistoryItem[]): DaySummary[] {
  const map = new Map<string, HistoryItem[]>();

  for (const h of history) {
    const d = new Date(h.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(h);
  }

  const days: DaySummary[] = [];
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

  for (const [dateKey, events] of map) {
    const d = new Date(dateKey + "T12:00:00");
    let scoreDelta = 0;
    let choresCompleted = 0;
    let choresFailed = 0;
    let rulesBroken = 0;
    let streakBonus = 0;

    for (const e of events) {
      scoreDelta += e.points;
      if (e.type === "chore_completed") choresCompleted++;
      if (e.type === "chore_overdue") choresFailed++;
      if (e.type === "rule_broken") rulesBroken++;
      if (e.type === "rule_streak") streakBonus += e.points;
    }

    days.push({
      date: dateKey,
      weekday: weekdays[d.getDay()] ?? "???",
      scoreDelta,
      choresCompleted,
      choresFailed,
      rulesBroken,
      streakBonus,
      events,
    });
  }

  days.sort((a, b) => b.date.localeCompare(a.date));
  return days;
}

function DayRow({ day, isExpanded, onToggle }: { day: DaySummary; isExpanded: boolean; onToggle: () => void }) {
  const d = new Date(day.date + "T12:00:00");
  const monthDay = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-app-surface hover:bg-app-surface-raised transition-colors cursor-pointer text-left"
      >
        {/* Expand icon */}
        <span className="shrink-0 text-app-text-muted w-4">
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>

        {/* Date + Weekday */}
        <div className="w-20 shrink-0">
          <span className="text-xs font-semibold text-app-text">{monthDay}</span>
          <span className="text-xs text-app-text-muted ml-1">{day.weekday}</span>
        </div>

        {/* Stats badges */}
        <div className="flex-1 flex items-center gap-1.5 flex-wrap min-w-0">
          {day.choresCompleted > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-app-success/15 text-app-success">
              ✅ {day.choresCompleted}
            </span>
          )}
          {day.choresFailed > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-app-warning/15 text-app-warning">
              ⏰ {day.choresFailed}
            </span>
          )}
          {day.rulesBroken > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-app-danger/15 text-app-danger">
              ⚠️ {day.rulesBroken}
            </span>
          )}
          {day.streakBonus !== 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-app-info/15 text-app-info">
              🔥 +{day.streakBonus}
            </span>
          )}
        </div>

        {/* Score delta */}
        <span className={`text-sm font-bold shrink-0 tabular-nums ${day.scoreDelta >= 0 ? "text-app-success" : "text-app-danger"}`}>
          {day.scoreDelta >= 0 ? "+" : ""}{day.scoreDelta}
        </span>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="ml-6 mt-1 mb-2 space-y-1">
          {day.events.map((h, i) => {
            const style = getEventStyle(h.type);
            return (
              <div key={i} className={`flex items-center gap-2 px-2 py-1 rounded-md ${style.bg} text-xs`}>
                <span className="shrink-0">{style.emoji}</span>
                <span className="flex-1 min-w-0 truncate text-app-text">{formatEventLabel(h)}</span>
                <span className="text-[10px] text-app-text-muted shrink-0">
                  {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className={`font-semibold shrink-0 ${h.points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                  {h.points >= 0 ? "+" : ""}{h.points}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminActivityHistory() {
  const { jwt, clientId } = useParams();
  const navigate = useNavigate();
  const { decryptValue } = useEncryption(clientId);

  const { data, loading, error } = useApi<{
    points: number;
    history: HistoryItem[];
  }>(`/api/admin/${jwt}/dashboard/${clientId}`);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!data?.history) return;
    (async () => {
      const decrypted = await Promise.all(
        data.history.map(async (h) => ({
          ...h,
          entity_name: h.entity_name ? await decryptValue(h.entity_name) : "",
          ref_name: h.ref_name ? await decryptValue(h.ref_name) : undefined,
          encrypted_note: h.encrypted_note ? await decryptValue(h.encrypted_note) : undefined,
          items: h.items
            ? await Promise.all(
                h.items.map(async (item) => ({
                  ...item,
                  name: item.name ? await decryptValue(item.name) : "",
                })),
              )
            : undefined,
        })),
      );
      setHistory(decrypted);
    })();
  }, [data?.history, decryptValue]);

  const days = useMemo(() => groupByDay(history), [history]);

  const toggleDay = (dateKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-lg font-bold text-app-text">📅 Activity History</h1>
      </div>

      {days.length === 0 ? (
        <EmptyState icon="📭" title="No activity yet" description="No history events to display." />
      ) : (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-2 space-y-0.5">
            {days.map((day) => (
              <DayRow
                key={day.date}
                day={day}
                isExpanded={expandedDays.has(day.date)}
                onToggle={() => toggleDay(day.date)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function ClientActivityHistory() {
  const { jwt } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { decryptValue, hasKey } = useEncryption(user?._key);

  const { data, loading, error } = useApi<{
    client: { name: string; icon: string };
    points: number;
    history: HistoryItem[];
    admin: { name: string; icon: string; description: string } | null;
  }>(`/api/client/${jwt}/dashboard`);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!data?.history) return;
    (async () => {
      const decrypted = await Promise.all(
        data.history.map(async (h) => ({
          ...h,
          entity_name: h.entity_name ? await decryptValue(h.entity_name) : "",
          ref_name: h.ref_name ? await decryptValue(h.ref_name) : undefined,
          encrypted_note: h.encrypted_note ? await decryptValue(h.encrypted_note) : undefined,
          items: h.items
            ? await Promise.all(
                h.items.map(async (item) => ({
                  ...item,
                  name: item.name ? await decryptValue(item.name) : "",
                })),
              )
            : undefined,
        })),
      );
      setHistory(decrypted);
    })();
  }, [data?.history, decryptValue]);

  const days = useMemo(() => groupByDay(history), [history]);

  const toggleDay = (dateKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-lg font-bold text-app-text">📅 Activity History</h1>
      </div>

      {days.length === 0 ? (
        <EmptyState icon="📭" title="No activity yet" description="Complete some chores to start earning points!" />
      ) : (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-2 space-y-0.5">
            {days.map((day) => (
              <DayRow
                key={day.date}
                day={day}
                isExpanded={expandedDays.has(day.date)}
                onToggle={() => toggleDay(day.date)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
